import React from 'react';
import { getUiVersion, isDev } from '../utils';
import { AppEventLogApplicationType } from '../models/settings';
import { getDefaultAuthValue } from '../contexts/AuthContext';
import { ApiService } from '../hooks';
import { HealthSystemDomainSettingsCreateEventLogCommand } from '../api/HealthSystemApi';

interface Props {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false, error: null };
  private apiService = new ApiService();

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error) {
    const log = this.buildActionLog(error);

    if (isDev()) {
      console.error('ErrorBoundary:', log);
    }

    // Make API call to save the error log
    const apiCall = this.apiService.api.settings.settingsCreateEventLog(log).catch((err) => {
      console.error('Failed to send error log:', err);
    });

    // Wait max 3 seconds for API call before redirecting
    const timeout = new Promise((resolve) => setTimeout(resolve, 3000));

    Promise.race([apiCall, timeout]).finally(() => {
      // Redirect to the error page
      window.location.replace(`/error?msg=${encodeURIComponent(error.message)}`);
    });
  }

  private buildActionLog(error: Error): HealthSystemDomainSettingsCreateEventLogCommand {
    const auth = getDefaultAuthValue?.();

    // Cast because the generated command requires `id`/`isAnalyzed` (the server assigns them) and
    // `userInfo.injector`/`userInfo.mobile` (a browser error has no device), none of which is sent here.
    return {
      userInfo: {
        user: {
          name: auth.userName,
          userId: auth.userId,
          farmId: auth.farmId,
          role: auth.role
        },
        requestIpAddress: null
      },
      application: {
        type: AppEventLogApplicationType.Web,
        version: getUiVersion(),
        languageIso: auth.locale
      },
      payload: {
        isError: true,
        action: 'HS Dashboard Error',
        message: error.message,
        stackTrace: error.stack,
        currentUrl: window.location.href
      },
      dateTime: new Date().toISOString()
    } as HealthSystemDomainSettingsCreateEventLogCommand;
  }

  public render() {
    return this.props.children;
  }
}

export default ErrorBoundary;
